import React, { FC, useCallback, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Breadcrumb, Button, Card, CardBody, CardHeader, Col, Row, Table } from 'reactstrap';
import * as OrderSummary from '../../../../../shared/orders/model';
import { ExtendedUser } from '../../../../../shared/user/extended';
import {
  ActionButton,
  ActionButtonModal,
  Breadcrumb as BreadcrumbComponent,
  DateTime,
  PermissionBoundary,
  RefundRequestModal,
  UserStateComponent,
} from '../../../../components';
import { useTranslation } from '../../../../translations';
import { Fetcher, isResourceError, useConfig, useConvention, useFetcher } from '../../../../utils';
import { captureError } from '../../../../utils/errorHandling';
import { OrderListItems, OrderPaymentTableItem, OrderUserInfo, OrderVouchers } from './components';
import { OrderAddProductModal } from './orderAddProductModal';
import { OrderFulfillModal } from './orderFulfillModal';
import { OrderRefundModal } from './orderRefundModal';
import { getOrderStatuses, OrderStatusInfo } from './utils';
import { DeteteOrderVoucherModal, OrderCreateVoucherModal } from './voucherModal';

type ModalType =
  | 'add_product'
  | 'create_voucher'
  | 'delete_voucher'
  | 'fulfill'
  | 'refund_request'
  | 'refund';

async function fetchOrder(
  orderId: number,
): Promise<{ order: OrderSummary.Order; user?: ExtendedUser }> {
  const order = await api.getOrderById(orderId);
  let user: ExtendedUser | undefined;

  try {
    user = await api.getExtendedUser(order.userId);
  } catch (error) {
    // The user may be deleted, but the order still exists.
    if (!isResourceError(error, 'User')) {
      throw error;
    }
  }

  return { order, user };
}

export const OrderInfo: FC = () => {
  const match = useRouteMatch<{ id: string }>();
  const orderId = Number.parseInt(match.params.id, 10);

  const fetcher = useFetcher(async () => await fetchOrder(orderId), [orderId]);

  return (
    <UserStateComponent>
      {fetcher.data ? (
        <OrderInfoDetails
          order={fetcher.data.order}
          refresh={fetcher.refresh}
          user={fetcher.data.user}
        />
      ) : (
        <Fetcher inline result={fetcher} />
      )}
    </UserStateComponent>
  );
};

interface PagSeguroCheckoutsProps {
  readonly orderId: number;
}

const PagSeguroCheckouts: FC<PagSeguroCheckoutsProps> = ({ orderId }) => {
  const pagSeguroCheckouts = useFetcher(
    async () => await api.getOrderPagSeguroCheckouts(orderId),
    [orderId],
  );

  const deactivateCheckout = useCallback(
    async (checkoutId: number) => {
      try {
        await api.deactivateOrderPagSeguroCheckout(orderId, checkoutId);
      } finally {
        pagSeguroCheckouts.refresh();
      }
    },
    [orderId, pagSeguroCheckouts],
  );

  const { ts } = useTranslation();

  return (
    pagSeguroCheckouts.success &&
    pagSeguroCheckouts.data.checkouts.length > 0 && (
      <Col lg={8} xs={12}>
        <Card className="mb-10">
          <CardHeader>PagSeguro Checkouts</CardHeader>
          <CardBody>
            <ul className="ps-3">
              {pagSeguroCheckouts.data.checkouts.map((checkout) => (
                <li key={checkout.id}>
                  <dl>
                    <dt>{ts('id')}</dt>
                    <dd>#{checkout.id}</dd>
                    <dt>PagSeguro ID</dt>
                    <dd>
                      {checkout.remote}{' '}
                      <Button className="btn-sm" href={checkout.payLink} tag="a" target="_blank">
                        {ts('open')}
                      </Button>
                    </dd>
                    <dt>{ts('fee.status')}</dt>
                    <dd>
                      {checkout.status}
                      {checkout.status === 'ACTIVE' && (
                        <>
                          {' '}
                          <Button
                            className="btn-sm"
                            onClick={async () => {
                              await deactivateCheckout(checkout.id);
                            }}
                          >
                            {ts('deactivate')}
                          </Button>
                        </>
                      )}
                    </dd>
                    <dt>{ts('expires_at')}</dt>
                    <dd>
                      <DateTime value={checkout.expiresAt} />
                    </dd>
                    <dt>{ts('created_at')}</dt>
                    <dd>
                      <DateTime value={checkout.createdAt} />
                    </dd>
                    <dt>{ts('updated_at')}</dt>
                    <dd>
                      <DateTime value={checkout.updatedAt} />
                    </dd>
                    {checkout.payments.length > 0 && (
                      <>
                        <dt>{ts('common.payments')}</dt>
                        <dd>
                          <ul>
                            {checkout.payments.map((payment) => (
                              <li key={payment.id}>
                                <dl>
                                  <dt>{ts('id')}</dt>
                                  <dd>#{payment.id}</dd>
                                  <dt>PagSeguro Transaction</dt>
                                  <dd>{payment.remoteOrde}</dd>
                                  <dt>PagSeguro ID</dt>
                                  <dd>{payment.remote}</dd>
                                  <dt>{ts('method')}</dt>
                                  <dd>{payment.method}</dd>
                                  <dt>{ts('fee.status')}</dt>
                                  <dd>{payment.status}</dd>
                                  <dt>{ts('created_at')}</dt>
                                  <dd>
                                    <DateTime value={payment.createdAt} />
                                  </dd>
                                  <dt>{ts('updated_at')}</dt>
                                  <dd>
                                    <DateTime value={payment.updatedAt} />
                                  </dd>
                                </dl>
                              </li>
                            ))}
                          </ul>
                        </dd>
                      </>
                    )}
                  </dl>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </Col>
    )
  );
};

interface OrderInfoDetailsProps {
  readonly order: OrderSummary.Order;
  readonly user?: ExtendedUser;
  readonly refresh: () => void;
}

const OrderInfoDetails: FC<OrderInfoDetailsProps> = ({ order, user, refresh }) => {
  const { ts } = useTranslation();
  const [modalOpen, setModalOpen] = useState<ModalType | undefined>(undefined);
  const [deleteVoucherId, setDeleteVoucherId] = useState<number>(0);
  const { pagSeguroEnabled } = useConfig();

  const status = getOrderStatuses(order);

  const modalData = {
    onCancel: () => {
      setModalOpen(undefined);
    },
    onSuccess: () => {
      refresh();
      setModalOpen(undefined);
    },
    order,
  };

  return (
    <>
      {modalOpen === 'fulfill' && <OrderFulfillModal {...modalData} />}
      {modalOpen === 'refund' && <OrderRefundModal {...modalData} />}
      {modalOpen === 'create_voucher' && <OrderCreateVoucherModal {...modalData} />}
      {modalOpen === 'delete_voucher' && (
        <DeteteOrderVoucherModal voucherId={deleteVoucherId} {...modalData} />
      )}
      {modalOpen === 'add_product' && user && <OrderAddProductModal user={user} {...modalData} />}
      {modalOpen === 'refund_request' && <RefundRequestModal {...modalData} />}

      <Row className="justify-content-center" id={`order${order.id}`}>
        <Col style={{ marginTop: '.15em', marginBottom: '2em' }} xs={12}>
          <BreadcrumbComponent
            items={[
              { text: ts('common.orders'), url: '/housekeeping/orders' },
              { active: true, text: `${ts('common.order')} #${order.id}` },
            ]}
          />
        </Col>
        <Col lg={11} xs={12}>
          <Breadcrumb className="button-list">
            <OrderPaymentState status={status} />
            <div className="separator" />
            <OrderActions order={order} refresh={refresh} setModalOpen={setModalOpen} />
            <PermissionBoundary inline requiredPermissions="audit:read">
              <Button
                color="secondary"
                outline
                tag={Link}
                to={`/housekeeping/auditlog/Order/${order.id}`}
              >
                {ts('common.order_history')}
              </Button>
            </PermissionBoundary>
          </Breadcrumb>
        </Col>
        <Col lg={6} xs={12}>
          <OrderListItems
            onUpdate={() => {
              refresh();
            }}
            openAddProductModal={() => {
              setModalOpen('add_product');
            }}
            order={order}
            status={status}
          />
        </Col>
        <Col className="mb-10" id="customerInfo" lg={5} xs={12}>
          <Card>
            <CardHeader>{ts('order_info.customer_info')}</CardHeader>
            <CardBody>
              {user ? (
                <OrderUserInfo user={user} />
              ) : (
                <div>{ts('order_info.user_order_is_deleted', order.userId)}</div>
              )}
            </CardBody>
          </Card>
        </Col>
        <OrderPaymets payments={order.payments} />
        {pagSeguroEnabled && <PagSeguroCheckouts orderId={order.id} />}
        <OrderVouchers
          openAddVoucherModal={() => {
            setModalOpen('create_voucher');
          }}
          openDeleteVoucherModal={(id) => {
            setDeleteVoucherId(id);
            setModalOpen('delete_voucher');
          }}
          order={order}
        />
      </Row>
    </>
  );
};

interface OrderActionsProps {
  readonly order: OrderSummary.Order;
  readonly refresh: () => void;
  readonly setModalOpen: (type: ModalType) => void;
}

const OrderActions: FC<OrderActionsProps> = ({ order, refresh, setModalOpen }) => {
  const { ts } = useTranslation();
  const convention = useConvention();
  const status = getOrderStatuses(order);

  function orderCancelled(): void {
    refresh();
    toast.success(ts('order_info.order_has_been_cancelled'));
  }

  if (status.includes(OrderStatusInfo.Cancelled) || convention.id !== order.conventionId) {
    return (
      <Button
        color="secondary"
        outline
        tag={Link}
        to={`/housekeeping/attendees/user/${order.userId}`}
      >
        {ts('order_info.customer_profile')}
      </Button>
    );
  }

  if (status.includes(OrderStatusInfo.Unpaid)) {
    return (
      <>
        <PermissionBoundary inline requiredPermissions="registration:cashier">
          <Button
            color="primary"
            id="markAsPaid"
            onClick={async () => {
              try {
                await (order.breakdown.total - order.breakdown.paid <= 0
                  ? api.settleOrder(order.id)
                  : api.pay('writeoff', 'none', order.userId));

                toast.success(ts('order_info.successfully_marked_order_as_paid'));
                refresh();
              } catch (error) {
                captureError(error as Error);
              }
            }}
          >
            {ts('order_info.mark_as_paid')}
          </Button>
        </PermissionBoundary>
        <Link className="button-link" to={`/housekeeping/attendees/user/${order.userId}`}>
          <Button color="secondary" outline>
            {ts('order_info.customer_profile')}
          </Button>
        </Link>
        <PermissionBoundary inline requiredPermissions="order:delete">
          <div className="separator" />
          <ActionButton
            action={async () => {
              await api.cancelOrder(order.id);
            }}
            color="danger"
            id="cancelOrder"
            onSuccess={orderCancelled}
            outline
          >
            {ts('order_info.cancel_order')}
          </ActionButton>
        </PermissionBoundary>
      </>
    );
  }

  const isUnfulfilled =
    status.includes(OrderStatusInfo.RequiresFulfillment) ||
    status.includes(OrderStatusInfo.PartiallyFulfilled);

  return (
    <>
      {isUnfulfilled && (
        <PermissionBoundary inline requiredPermissions="order:fulfill">
          <Button
            color="primary"
            id="markAsFulfilled"
            onClick={() => {
              setModalOpen('fulfill');
            }}
          >
            {ts('order_info.mark_as_fulfilled')}
          </Button>
        </PermissionBoundary>
      )}
      <Link className="button-link" to={`/housekeeping/attendees/user/${order.userId}`}>
        <Button color="secondary" outline>
          {ts('order_info.customer_profile')}
        </Button>
      </Link>
      {status.includes(OrderStatusInfo.Paid) && (
        <ActionButtonModal
          actionContent="common.resend"
          buttonContent="common.resend_email"
          color="primary"
          id="resendOrderConfirmation"
          onComplete={async () => {
            await api.resendOrderConfirmation(order.id);
            toast.success(ts('the_user_will_receive_an_email_in_their_inbox_shortly'));
          }}
          outline
          title="common.resend_order_email_confirmation"
        >
          {ts('confirm_order_resend_email')}
        </ActionButtonModal>
      )}
      {!status.includes(OrderStatusInfo.Refunded) && (
        <PermissionBoundary inline requiredPermissions="refund:create">
          <div className="separator" />
          <Button
            color="danger"
            id="refundRequestOrder"
            onClick={() => {
              setModalOpen('refund_request');
            }}
            outline
          >
            {ts('order_info.create_refund_request')}
          </Button>
          <Button
            color="danger"
            id="refundOrder"
            onClick={() => {
              setModalOpen('refund');
            }}
            outline
          >
            {ts('order_info.refund_order')}
          </Button>
        </PermissionBoundary>
      )}
    </>
  );
};

const OrderPaymets: FC<{ readonly payments?: OrderSummary.Payment[] }> = ({ payments }) => {
  const { ts } = useTranslation();
  if (!payments || payments.length === 0) {
    return null;
  }

  return (
    <Col lg={11} xs={12}>
      <Card className="mb-10">
        <CardHeader>{ts('common.payments')}</CardHeader>
        <CardBody>
          <Table>
            <thead>
              <tr>
                <th>{ts('common.amount')}</th>
                <th>{ts('common.status')}</th>
                <th>{ts('common.gateway')}</th>
                <th>
                  {ts('reference')} {ts('id')}
                </th>
                <th>{ts('common.date')}</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <OrderPaymentTableItem key={payment.id} payment={payment} />
              ))}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </Col>
  );
};

const OrderPaymentState: FC<{ readonly status: OrderStatusInfo[] }> = ({ status }) => {
  const { ts } = useTranslation();
  if (status.includes(OrderStatusInfo.Cancelled)) {
    return (
      <div className="text text-secondary" id="orderCancelled">
        {ts('common.cancelled')}
      </div>
    );
  }

  if (status.includes(OrderStatusInfo.Unpaid)) {
    return (
      <div className="text text-danger" id="orderNotPaid">
        {ts('order_info.not_paid')}
      </div>
    );
  }

  if (status.includes(OrderStatusInfo.PartiallyRefunded)) {
    return (
      <div className="text text-info" id="orderPartiallyRefunded">
        {ts('order_info.paid_but_partially_refunded')}
      </div>
    );
  }

  if (status.includes(OrderStatusInfo.Refunded)) {
    return (
      <div className="text text-info" id="orderRefunded">
        {ts('order_info.paid_but_refunded')}
      </div>
    );
  }

  return (
    <div className="text text-success" id="orderPaid">
      {ts('common.paid')}
    </div>
  );
};
